import React, { useState } from "react";
import { PrivateOfficeUpdateRequest } from "@/services/private-office-service/types";
import { getPreviousImageUrl, isImageRequest } from "@/utils/image-utils";
import { X, User, Calendar, CalendarX, ImageOff } from "lucide-react";

interface RejectedImageRequestProps {
  request: PrivateOfficeUpdateRequest;
}

export const RejectedImageRequest: React.FC<RejectedImageRequestProps> = ({
  request,
}) => {
  const [showImageModal, setShowImageModal] = useState(false);

  if (!isImageRequest(request.fields_to_update)) {
    return null;
  }

  const previousImageUrl = getPreviousImageUrl(request.previous_values);

  return (
    <div className="bg-white rounded-lg border border-red-200 p-6 space-y-6">
      <div className="border-b border-red-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <X className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Solicitação de Alteração de Imagem - {request.office_info.name}
            </h3>
            <p className="text-sm text-gray-600">
              Solicitado por {request.requested_by.username} em{" "}
              {new Date(request.created_at).toLocaleDateString("pt-BR")}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Alerta de reprovação */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-red-800 font-medium">
                Solicitação Reprovada
              </h4>
              <p className="text-red-700 text-sm mt-1">
                Esta solicitação de alteração de imagem foi reprovada.
              </p>
            </div>
          </div>
        </div>

        {/* Motivo da reprovação */}
        {request.rejection_reason && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">
              Motivo da Reprovação
            </h4>
            <p className="text-gray-700 text-sm">{request.rejection_reason}</p>
          </div>
        )}

        {/* Imagem anterior */}
        {previousImageUrl ? (
          <div className="text-center">
            <h4 className="font-medium text-gray-900 mb-4">
              Imagem que seria substituída
            </h4>
            <div className="relative max-w-sm mx-auto">
              <div
                className="group aspect-video rounded-xl overflow-hidden shadow-lg ring-2 ring-red-200 cursor-pointer hover:ring-red-300 transition-all duration-200 relative"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowImageModal(true);
                }}
              >
                <img
                  src={previousImageUrl}
                  alt="Imagem anterior"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                />
                {/* Overlay de reprovação */}
                <div className="absolute inset-0 bg-red-500 bg-opacity-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    Reprovada
                  </div>
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center shadow-lg">
                <X className="w-3 h-3 text-white" />
              </div>
              <div className="text-center mt-3">
                <p className="text-sm text-gray-600 font-medium">
                  Imagem atual (clique para ampliar)
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ImageOff className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-600">
              A imagem anterior não está disponível para visualização.
            </p>
          </div>
        )}
      </div>

      {/* Detalhes */}
      <div className="pt-4 border-t border-gray-200">
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <User className="w-4 h-4" />
            <span>
              Solicitado por:{" "}
              <span className="font-medium">
                {request.requested_by.username}
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>
              Data:{" "}
              <span className="font-medium">
                {new Date(request.created_at).toLocaleDateString("pt-BR")}
              </span>
            </span>
          </div>
          {request.reviewed_at && request.reviewed_by && (
            <div className="flex items-center gap-2 text-gray-600">
              <CalendarX className="w-4 h-4" />
              <span>
                Reprovada em:{" "}
                <span className="font-medium">
                  {new Date(request.reviewed_at).toLocaleDateString("pt-BR")}
                  {` por ${request.reviewed_by.name}`}
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Modal para visualizar imagem ampliada */}
      {showImageModal && previousImageUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setShowImageModal(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh] mx-4">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <img
              src={previousImageUrl}
              alt="Imagem anterior"
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};
